import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  IconButton,
  CircularProgress,
  Autocomplete,
  Paper,
  Divider,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { useApp } from '../../context/AppContext';
import { supabase } from '../../lib/supabase';
import { useSnackbar } from 'notistack';

const MyDefinitionDialog = ({ open, onClose }) => {
  const { terms, subjects } = useApp();
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);
  const [searching, setSearching] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);
  const [selectedSubject, setSelectedSubject] = useState('');
  const [selectedTerm, setSelectedTerm] = useState(null);
  
  const [formData, setFormData] = useState({
    custom_definition: '',
    korean_definition: '',
    keywords: ''
  });

  // 선택된 과목의 용어만 필터링
  const filteredTerms = useMemo(() => {
    if (!selectedSubject) return [];
    return terms.filter(term => term.subject === selectedSubject);
  }, [terms, selectedSubject]);

  const resetForm = () => {
    setFormData({
      custom_definition: '',
      korean_definition: '',
      keywords: ''
    });
    setSelectedTerm(null);
    setSelectedSubject('');
    setSuccess(false);
    setError(null);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleChange = (field) => (event) => {
    setFormData({
      ...formData,
      [field]: event.target.value
    });
  };

  // 과목 선택 핸들러
  const handleSubjectChange = (event) => {
    setSelectedSubject(event.target.value);
    setSelectedTerm(null); // 과목이 변경되면 선택된 용어 초기화
  };

  // 이미 정의한 용어인지 확인
  const checkExistingDefinition = async (termId) => {
    setSearching(true);
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const userId = sessionData?.session?.user?.id;
      
      if (!userId) {
        throw new Error('로그인이 필요합니다.');
      }
      
      const { data, error } = await supabase
        .from('my_definitions')
        .select('*')
        .eq('user_id', userId)
        .eq('term_id', termId)
        .maybeSingle();
      
      if (error) {
        throw new Error(`조회 실패: ${error.message}`);
      }
      
      if (data) {
        setFormData({
          custom_definition: data.custom_definition || '',
          korean_definition: data.korean_definition || '',
          keywords: (data.keywords || []).join(', ')
        });
      }
      
    } catch (err) {
      console.error('❌ 기존 정의 확인 실패:', err);
    } finally {
      setSearching(false);
    }
  };

  const handleSelectTerm = (newValue) => {
    setSelectedTerm(newValue);
    if (newValue?.id) {
      checkExistingDefinition(newValue.id);
    }
  };

  const handleSubmit = async () => {
    if (!selectedTerm || !formData.custom_definition) {
      setError('용어 선택과 나만의 정의는 필수 입력 항목입니다.');
      return;
    }

    setLoading(true);
    setError(null);
    
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const userId = sessionData?.session?.user?.id;
      
      if (!userId) {
        throw new Error('로그인이 필요합니다.');
      }

      const keywordsArray = formData.keywords
        ? formData.keywords.split(',').map(k => k.trim()).filter(Boolean)
        : [];
      
      const { data, error } = await supabase
        .from('my_definitions')
        .upsert({
          user_id: userId,
          term_id: selectedTerm.id,
          custom_definition: formData.custom_definition,
          korean_definition: formData.korean_definition || null,
          keywords: keywordsArray,
          updated_at: new Date().toISOString()
        }, { onConflict: 'user_id,term_id' })
        .select();
      
      if (error) {
        throw new Error(`저장 실패: ${error.message}`);
      }
      
      console.log('✅ 나만의 정의 추가 성공:', data);
      setSuccess(true);
      enqueueSnackbar('나만의 정의가 저장되었습니다.', { variant: 'success' });
      
      setTimeout(() => {
        if (success) handleClose();
      }, 3000);
      
    } catch (err) {
      console.error('❌ 나만의 정의 추가 실패:', err);
      setError(err.message);
      enqueueSnackbar('나만의 정의 저장에 실패했습니다.', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={loading ? null : handleClose} 
      maxWidth="sm" 
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: 8
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        pb: 1
      }}>
        <Typography variant="h6" component="div" fontWeight="bold">
          나만의 정의 추가
        </Typography>
        <IconButton 
          edge="end" 
          onClick={handleClose} 
          disabled={loading}
          aria-label="close"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <DialogContent dividers>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          {success && (
            <Box sx={{ 
              bgcolor: 'success.light', 
              p: 2, 
              borderRadius: 1,
              mb: 2
            }}>
              <Typography color="success.dark" fontWeight="medium">
                나만의 정의가 성공적으로 추가되었습니다!
              </Typography>
            </Box>
          )}
          
          {error && (
            <Box sx={{ 
              bgcolor: 'error.light', 
              p: 2, 
              borderRadius: 1,
              mb: 2
            }}>
              <Typography color="error" fontWeight="medium">
                {error}
              </Typography>
            </Box>
          )}

          {/* 과목 선택 */}
          <FormControl fullWidth required>
            <InputLabel>과목 선택</InputLabel>
            <Select
              value={selectedSubject}
              onChange={handleSubjectChange}
              label="과목 선택"
              disabled={loading}
            >
              {(subjects || []).map(subject => (
                <MenuItem key={subject} value={subject}>
                  {subject}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* 용어 선택 (선택된 과목의 용어만 표시) */}
          <Autocomplete
            value={selectedTerm}
            onChange={(event, newValue) => handleSelectTerm(newValue)}
            options={filteredTerms}
            getOptionLabel={(option) => option.term}
            renderInput={(params) => (
              <TextField
                {...params}
                label="용어 선택"
                required
                disabled={!selectedSubject}
                helperText={!selectedSubject ? "과목을 먼저 선택해주세요" : ""}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {searching ? (
                        <CircularProgress color="inherit" size={20} />
                      ) : (
                        params.InputProps.endAdornment
                      )}
                    </>
                  ),
                }}
              />
            )}
            renderOption={(props, option) => (
              <li {...props} key={option.id}>
                <Box>
                  <Typography variant="subtitle1">
                    {option.term}
                  </Typography>
                </Box>
              </li>
            )}
            disabled={loading || !selectedSubject}
            noOptionsText="해당 과목에 용어가 없습니다"
            isOptionEqualToValue={(option, value) => option.id === value?.id}
          />

          {selectedTerm && (
            <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                원본 정의
              </Typography>
              <Typography variant="body1">
                {selectedTerm.definition}
              </Typography>
            </Paper>
          )}

          <TextField
            fullWidth
            multiline
            rows={4}
            label="나만의 정의"
            value={formData.custom_definition}
            onChange={handleChange('custom_definition')}
            required
            disabled={loading}
            helperText="용어에 대한 나만의 정의를 작성해주세요"
          />
          
          <TextField
            fullWidth
            multiline
            rows={3}
            label="한국어 설명 (선택사항)"
            value={formData.korean_definition}
            onChange={handleChange('korean_definition')}
            disabled={loading}
            helperText="용어에 대한 한국어 설명을 추가할 수 있습니다"
          />

          <TextField
            fullWidth
            label="키워드 (쉼표로 구분)"
            value={formData.keywords}
            onChange={handleChange('keywords')}
            disabled={loading}
            helperText="키워드는 쉼표(,)로 구분하여 입력하세요"
          />
          
          {formData.keywords && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {formData.keywords.split(',').map((keyword, index) => (
                keyword.trim() && (
                  <Chip 
                    key={index} 
                    label={keyword.trim()} 
                    size="small" 
                    color="primary" 
                    variant="outlined" 
                  />
                )
              ))}
            </Box>
          )}
        </Box>
      </DialogContent>
      
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button 
          onClick={handleClose} 
          color="inherit"
          disabled={loading}
        >
          취소
        </Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained" 
          disabled={loading || !selectedTerm || !formData.custom_definition}
          startIcon={loading && <CircularProgress size={16} color="inherit" />}
        >
          {loading ? '저장 중...' : '저장'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MyDefinitionDialog;